import { hexToRGBA } from '@/config/settings';

import type { ChartTheme } from '../theme';

import { perBarOutlines } from './barOutlines';
import {
    axisScaleId,
    CHART_ISOLATED_POINT_RADIUS,
    CHART_LINE_TENSION,
    CHART_LINE_WIDTH,
    CHART_POINT_BORDER_WIDTH,
    CHART_POINT_HOVER_BORDER_WIDTH,
    CHART_POINT_HOVER_RADIUS,
    CHART_POINT_RADIUS,
    CHART_SCATTER_POINT_RADIUS,
    CHART_SERIES_DASH_PATTERN,
    DEFAULT_FILL_OPACITY,
} from './constants';
import {
    fadeColor,
    inProgressBarStyle,
    inProgressIndex,
    inProgressPointStyle,
} from './inProgressBucket';
import { bandStyle, gradientFillStyle, gradientLineStyle, uncertainStyle } from './lineStyles';
import type { MetricChartData } from './optionTypes';
import type { MetricPointMarker } from './seriesStyleTypes';
import type { MetricSeriesSpec } from './seriesTypes';
import {
    coalesceStackedNullsAsZero,
    resolveLineFill,
    STACK_ID_LEFT,
    STACK_ID_RIGHT,
    stackedDatasetsForAxis,
} from './stackedDatasets';
import { themedSeries } from './themedSeries';
import type { MetricChartKind, MetricChartSpec } from './types';

/**
 * Joins a series' day-keyed points onto the chart's day axis.
 *
 * Returns one entry per day in `days`, in that order. Days the series has no
 * point for become `null`, which Chart.js renders as a gap (line datasets set
 * `spanGaps: false`). A day appearing twice in the series keeps the last value.
 *
 * This is what lets series from different sources share one x axis without the
 * caller aligning them, and what removes the class of bug where a series is read
 * positionally against an index it does not actually correspond to.
 */
export function joinSeriesToDays(series: MetricSeriesSpec, days: string[]): (number | null)[] {
    const byDay = new Map<string, number | null>();

    for (const point of series.data) {
        byDay.set(point.day, point.value);
    }

    return days.map((day) => {
        const value = byDay.get(day);
        // `undefined` (no point for this day) and an explicit `null` both mean gap.
        return value === undefined ? null : value;
    });
}

/**
 * Per-point radii for a line series: the line-width default everywhere, and the
 * larger isolated radius on any point with a gap on both sides.
 *
 * Chart.js draws a line dataset as segments between consecutive non-null points, so
 * a point whose neighbours are both `null` produces no segment. At a radius that
 * vanishes into the line it therefore renders nothing, and the reader sees empty
 * space where there is in fact a measurement — the one case where the point is the
 * only thing carrying the value.
 *
 * The ends of the series count as a missing neighbour, so a single leading or
 * trailing measurement is treated as isolated too.
 */
export function pointRadii(data: (number | null)[]): number[] {
    return data.map((value, index) => {
        if (value === null) {
            return CHART_POINT_RADIUS;
        }

        const hasNeighbour = data[index - 1] != null || data[index + 1] != null;

        return hasNeighbour ? CHART_POINT_RADIUS : CHART_ISOLATED_POINT_RADIUS;
    });
}

/** A line series' points for its `marker`, in place of the default markers. */
function markerStyle(marker: MetricPointMarker, color: string, theme: ChartTheme) {
    const fill =
        marker.fill === 'surface' ? theme.surface : marker.fill === 'none' ? 'transparent' : color;
    return {
        pointRadius: marker.radius,
        pointHoverRadius: marker.hoverRadius ?? CHART_POINT_HOVER_RADIUS,
        pointBackgroundColor: fill,
        pointBorderColor: color,
        pointBorderWidth: marker.borderWidth ?? 1,
        ...(marker.filledOnHover
            ? { pointHoverBackgroundColor: color, pointHoverBorderColor: color }
            : {}),
    };
}

/**
 * Builds Chart.js data from a spec. Series keep their spec order, so legend
 * indices line up with `spec.series` indices. Series colours are drawn as `theme` has them
 * (`themedSeries`).
 */
export function buildMetricChartData<TKind extends MetricChartKind>(
    spec: MetricChartSpec<TKind>,
    theme: ChartTheme
): MetricChartData<TKind> {
    // Per y axis, extra axes included: how many filled stacked series it already has.
    const filledSeriesCountByAxis: Record<string, number> = {};
    const progressIndex = inProgressIndex(spec);

    const datasets = spec.series.map((specSeries) => {
        const series = themedSeries(specSeries, theme);
        // The colour before `opacity`, for the styles that set their own.
        const baseColor = theme.color(specSeries.color);
        const axisKey = series.axis === 'right' ? 'right' : (series.axis ?? 'left');
        const axisSpec =
            axisKey === 'right'
                ? spec.right
                : axisKey === 'left'
                  ? spec.left
                  : spec.extraAxes?.[axisKey];
        const isStacked = axisSpec?.stacked === true;
        // A series may override the chart's kind, which is what makes a mixed chart
        // possible. Chart.js reads `type` per dataset.
        const kind = series.type ?? spec.kind;

        const base = {
            label: series.label,
            data: joinSeriesToDays(series, spec.days),
            borderColor: series.color,
            // A filled series needs a translucent background so the bands beneath it
            // stay readable; an unfilled one only uses this for the legend swatch.
            backgroundColor: series.fill
                ? hexToRGBA(series.color, series.fillOpacity ?? DEFAULT_FILL_OPACITY)
                : series.color,
            yAxisID: axisScaleId(axisKey),
            ...(series.excludeFromCsv ? { excludeFromCsv: true } : {}),
            ...(series.type ? { type: series.type } : {}),
            ...(series.order === undefined ? {} : { order: series.order }),
            ...(isStacked ? { stack: axisKey === 'right' ? STACK_ID_RIGHT : STACK_ID_LEFT } : {}),
        };

        if (kind === 'bar') {
            const opaque = spec.days.map(
                (_day, index) => series.barColors?.[index] ?? series.color
            );
            const fills =
                series.barOpacity === undefined
                    ? opaque
                    : opaque.map((color) => fadeColor(color, series.barOpacity ?? 1));
            return {
                ...base,
                // Per-bar colour wins over the series colour where supplied, so a bar
                // can carry a threshold meaning while the legend swatch keeps carrying
                // the series identity.
                ...(series.barColors || series.barOpacity !== undefined
                    ? { backgroundColor: fills }
                    : {}),
                barPercentage: series.barPercentage,
                categoryPercentage: series.categoryPercentage,
                // Without `borderSkipped: false` a stacked bar rounds only the top of the
                // stack, not each segment.
                ...(series.borderRadius === undefined
                    ? {}
                    : { borderRadius: series.borderRadius, borderSkipped: false }),
                ...(series.barBorder
                    ? {
                          borderColor: series.barBorder.color,
                          borderWidth: series.barBorder.width,
                      }
                    : {}),
                ...(series.barOutlines
                    ? perBarOutlines(series, series.barOutlines, spec.days.length)
                    : {}),
                // Last, because it restates every bar's border with the marker applied.
                ...(progressIndex >= 0 ? inProgressBarStyle(series, fills, progressIndex) : {}),
            };
        }

        // A series without a connecting line is a scatter, so its points have to
        // be given a visible radius — the line default of 0 would draw nothing.
        const showLine = series.showLine !== false;
        const radii = showLine ? pointRadii(base.data) : CHART_SCATTER_POINT_RADIUS;
        const filledOnAxis = filledSeriesCountByAxis[axisKey] ?? 0;
        const fill = resolveLineFill(series, isStacked, filledOnAxis);

        if (series.fill === true && isStacked) {
            filledSeriesCountByAxis[axisKey] = filledOnAxis + 1;
        }

        return {
            ...base,
            fill,
            // Overrides the flat `backgroundColor` from `base`. Only for a series that is
            // actually filled — a gradient on an unfilled line would only colour the legend
            // swatch, with nothing on the plot to show for it.
            ...gradientFillStyle(series, baseColor, theme),
            showLine,
            // A day with no measurement must break the line rather than be bridged
            // over. This is the Chart.js default, but stated here because the whole
            // point of the continuous day axis built by `dayAxis` is to put those
            // null days on the chart — a `spanGaps: true` picked up from a global
            // default would silently undo it. Only a series that asks (`bridgeGaps`) spans.
            spanGaps: series.bridgeGaps === true,
            ...(series.stepped ? { stepped: true as const } : {}),
            tension: series.stepped ? 0 : (series.tension ?? CHART_LINE_TENSION),
            borderWidth: series.lineWidth ?? CHART_LINE_WIDTH,
            // Chart.js points inherit `backgroundColor`, which for a filled series is the
            // translucent fill — or, with `fillGradient`, a gradient that is nearly invisible
            // over most of the plot. The point has to carry the series' own colour, or the
            // temperature reading on a filled band renders as nothing.
            pointBackgroundColor: series.color,
            pointRadius: radii,
            pointHoverRadius: CHART_POINT_HOVER_RADIUS,
            pointBorderWidth: CHART_POINT_BORDER_WIDTH,
            pointHoverBorderWidth: CHART_POINT_HOVER_BORDER_WIDTH,
            ...(series.dashed ? { borderDash: CHART_SERIES_DASH_PATTERN } : {}),
            ...uncertainStyle(series, baseColor, theme),
            ...bandStyle(series),
            ...(series.marker ? markerStyle(series.marker, series.color, theme) : {}),
            // Last: a threshold-coloured line colours its points by height, marker or not.
            ...gradientLineStyle(series, theme),
            ...(progressIndex >= 0
                ? inProgressPointStyle(
                      series.color,
                      radii,
                      spec.days.length,
                      progressIndex,
                      theme.surface
                  )
                : {}),
        };
    });

    if (spec.kind === 'line') {
        if (spec.left.stacked === true) {
            coalesceStackedNullsAsZero(stackedDatasetsForAxis(datasets, 'left', STACK_ID_LEFT));
        }
        if (spec.right?.stacked === true) {
            coalesceStackedNullsAsZero(stackedDatasetsForAxis(datasets, 'right', STACK_ID_RIGHT));
        }
    }

    // Chart.js types a chart's datasets by the chart's kind, but a series may override its
    // own `type` (a line over bars), so the datasets are not a `ChartDataset<TKind>` in
    // general and no check could prove it. Asserting here keeps the generic signature,
    // which is what lets the call sites stay cast-free.
    return {
        labels: spec.days,
        datasets,
    } as unknown as MetricChartData<TKind>;
}
